import React, { useState } from "react";
import { Button } from "components/ui";
import { useTranslation } from "lib/i18n/use-translation";
import { splitQuestionTitle } from "./questionTitle";
import { parseEquivalents } from "./equivalentsTable";
import AssessmentUpliftHeader from "./AssessmentUpliftHeader";
import ProgressPill, { ProgressPillProps } from "./ProgressPill";
import AssessmentStepTransition from "./AssessmentStepTransition";
import styles from "./AssessmentQuestionUplift.module.scss";
import stepperStyles from "./AssessmentQuestionDurationUplift.module.scss";
import countStyles from "./AssessmentQuestionCountUplift.module.scss";

export interface AssessmentQuestionCountUpliftProps {
  stepKey: string;
  titleHtml?: string | null;
  subtitleHtml?: string | null;
  unitLabel: string;
  hasEquivalentsTable?: boolean;
  defaultCount?: number;
  onSubmit: (count: number) => void;
  onBack?: () => void;
  onClose: () => void;
  progressPill?: ProgressPillProps | null;
}

const COUNT_MAX = 99;
const MAX_DIGITS = 2;

// Backend copy uses a curly apostrophe ("doesn’t"); normalize both sides
// before comparing so authored duplicates of this hint are still detected.
const normalizeApostrophes = (value: string) => value.replace(/[‘’]/g, "'");

const AssessmentQuestionCountUplift: React.FC<
  AssessmentQuestionCountUpliftProps
> = ({
  stepKey,
  titleHtml,
  subtitleHtml,
  unitLabel,
  hasEquivalentsTable = false,
  defaultCount = 0,
  onSubmit,
  onBack,
  onClose,
  progressPill,
}) => {
  const { t } = useTranslation(["assessment"]);
  const BEST_GUESS_HINT = t("assessment:question.estimateHint");
  const { eyebrow, title } = splitQuestionTitle(titleHtml);
  const rawQuestionHint = subtitleHtml
    ? subtitleHtml.replace(/<[^>]+>/g, "").trim()
    : null;
  // A question with an equivalents table renders its subtitleHtml as that
  // table, not as a plain hint line above it. Some questions also author
  // the best-guess hint verbatim as their own subtitleHtml — don't repeat it.
  const questionHint =
    !hasEquivalentsTable &&
    rawQuestionHint &&
    normalizeApostrophes(rawQuestionHint.toLowerCase()) !==
      normalizeApostrophes(BEST_GUESS_HINT.toLowerCase())
      ? rawQuestionHint
      : null;
  const equivalents =
    hasEquivalentsTable && subtitleHtml
      ? parseEquivalents(subtitleHtml)
      : null;
  // A subtitle that is not an equivalents list is shown as an ordinary hint
  // rather than a broken table.
  const shownHint =
    questionHint ??
    (hasEquivalentsTable && !equivalents ? rawQuestionHint : null);

  const [count, setCount] = useState(defaultCount);
  const [countText, setCountText] = useState(String(defaultCount));

  const commitCount = (next: number) => {
    setCount(next);
    setCountText(String(next));
  };

  const decrement = () => commitCount(Math.max(0, count - 1));
  const increment = () => commitCount(Math.min(COUNT_MAX, count + 1));

  const handleCountChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setCountText(
      event.target.value.replace(/[^0-9]/g, "").slice(0, MAX_DIGITS)
    );
  const handleCountBlur = () =>
    commitCount(
      countText === ""
        ? 0
        : Math.min(COUNT_MAX, Math.max(0, Number(countText)))
    );

  const handleContinue = () => onSubmit(count);

  return (
    <div
      className={styles.container}
      data-testid="assessment-question-count-uplift"
    >
      <AssessmentUpliftHeader onBack={onBack} onClose={onClose} />

      {progressPill && (
        <div className={styles["pill-row"]}>
          <ProgressPill {...progressPill} />
        </div>
      )}

      <div className={styles.body}>
        <AssessmentStepTransition stepKey={stepKey}>
          <div className={styles.content}>
            {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
            <h1 className={styles.title}>{title}</h1>
            {shownHint && (
              <p className={stepperStyles["question-hint"]}>
                {shownHint}
              </p>
            )}

            {equivalents && (
              <div className={countStyles.equivalents}>
                {equivalents.headline && (
                  <p className={countStyles["equivalents-headline"]}>
                    {equivalents.headline}
                  </p>
                )}
                {equivalents.rows.map((row, index) => (
                  <div key={index} className={countStyles["equivalents-row"]}>
                    <p className={countStyles["equivalents-qty"]}>
                      {row.qty}
                    </p>
                    <p className={countStyles["equivalents-desc"]}>
                      {row.desc}
                    </p>
                    <p className={countStyles["equivalents-vol"]}>
                      {row.vol}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <div
              className={stepperStyles.stepper}
              data-testid="assessment-count-stepper"
            >
              <button
                type="button"
                aria-label="decrease"
                className={stepperStyles["stepper-button"]}
                disabled={count <= 0}
                onClick={decrement}
              >
                {"−"}
              </button>
              <div className={stepperStyles["stepper-value-group"]}>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={MAX_DIGITS}
                  aria-label="count value"
                  className={stepperStyles["stepper-value"]}
                  value={countText}
                  onChange={handleCountChange}
                  onBlur={handleCountBlur}
                />
                <p className={stepperStyles["stepper-label"]}>{unitLabel}</p>
              </div>
              <button
                type="button"
                aria-label="increase"
                className={stepperStyles["stepper-button"]}
                disabled={count >= COUNT_MAX}
                onClick={increment}
              >
                {"+"}
              </button>
            </div>

            <p className={stepperStyles.hint}>{BEST_GUESS_HINT}</p>

            <Button
              variant="enclosed-primary"
              monochromeColour="black"
              className={stepperStyles["continue-button"]}
              onClick={handleContinue}
            >
              {t("assessment:question.continue")}
            </Button>
          </div>
        </AssessmentStepTransition>
      </div>
    </div>
  );
};

export default AssessmentQuestionCountUplift;
